// GroupByPanel - UI for configuring table grouping and aggregations
// Drag & Drop interface similar to Excel Pivot Tables

import React, { useState } from 'react';
import type { GroupConfig, TableColumn, AggregationType } from '@/app/types/table';
import { Button } from '@/app/components/ui/button';
import { Label } from '@/app/components/ui/label';
import { Checkbox } from '@/app/components/ui/checkbox';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { X, GripVertical, Plus, Layers } from 'lucide-react';
import { getAggregationLabel, isAggregationCompatible } from '@/app/utils/aggregationUtils';
import { useLanguage } from '@/app/contexts/LanguageContext';

interface GroupByPanelProps {
  groupConfig: GroupConfig;
  columns: TableColumn[];
  onUpdateGroupConfig: (config: GroupConfig) => void;
  onClose: () => void;
}

export function GroupByPanel({ 
  groupConfig, 
  columns, 
  onUpdateGroupConfig,
  onClose 
}: GroupByPanelProps) {
  const { t } = useLanguage();
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);

  // Available columns (not yet grouped)
  const availableColumns = columns.filter(
    col => !groupConfig.columnIds.includes(col.id) && !col.isHeader
  );

  // Grouped columns in order
  const groupedColumns = groupConfig.columnIds
    .map(id => columns.find(col => col.id === id))
    .filter((col): col is TableColumn => col !== undefined);

  const handleAddGroupColumn = (columnId: string) => {
    onUpdateGroupConfig({
      ...groupConfig,
      columnIds: [...groupConfig.columnIds, columnId],
      enabled: true, // Auto-enable when adding first column
    });
  };

  const handleRemoveGroupColumn = (columnId: string) => {
    const newColumnIds = groupConfig.columnIds.filter(id => id !== columnId);
    onUpdateGroupConfig({
      ...groupConfig,
      columnIds: newColumnIds,
      enabled: newColumnIds.length > 0, // Disable if no columns left
    });
  };

  const handleMoveGroupColumn = (fromIndex: number, toIndex: number) => {
    const newColumnIds = [...groupConfig.columnIds];
    const [removed] = newColumnIds.splice(fromIndex, 1);
    newColumnIds.splice(toIndex, 0, removed);
    onUpdateGroupConfig({
      ...groupConfig,
      columnIds: newColumnIds,
      enabled: newColumnIds.length > 0, // Disable if no columns left
    });
  };

  const handleAggregationChange = (columnId: string, aggregation: AggregationType) => {
    onUpdateGroupConfig({
      ...groupConfig,
      aggregations: {
        ...groupConfig.aggregations,
        [columnId]: aggregation,
      },
    });
  };

  const handleToggleSubtotals = (enabled: boolean) => {
    onUpdateGroupConfig({
      ...groupConfig,
      showSubtotals: enabled,
    });
  };

  const handleToggleGrandTotal = (enabled: boolean) => {
    onUpdateGroupConfig({
      ...groupConfig,
      showGrandTotal: enabled,
    });
  };

  const handleClearAll = () => {
    onUpdateGroupConfig({
      ...groupConfig,
      columnIds: [],
      aggregations: {},
      enabled: false,
    });
  };

  return (
    <div 
      className="border-l flex flex-col h-full" 
      style={{ 
        width: '320px', 
        flexShrink: 0,
        backgroundColor: 'var(--md-surface-container-low)',
        borderColor: 'var(--md-outline-variant)',
      }}
    >
      {/* Header */}
      <div 
        className="p-4 border-b flex items-center justify-between"
        style={{
          backgroundColor: 'var(--md-surface-container)',
          borderColor: 'var(--md-outline-variant)',
        }}
      >
        <div className="flex items-center gap-2">
          <Layers className="w-5 h-5" style={{ color: 'var(--md-primary)' }} />
          <h3 className="font-semibold" style={{ color: 'var(--md-on-surface)' }}>{t.table.groupByPanel.title}</h3>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4 overflow-y-auto flex-1">
        
        {/* Group By Area */}
        <div>
          <Label className="text-sm font-medium mb-2 block">{t.table.groupByPanel.groupBy}</Label>
          
          {groupedColumns.length === 0 ? (
            <div 
              className="border-2 border-dashed rounded-md p-4 text-center text-sm text-gray-500"
            >
              {t.table.groupByPanel.dragInstruction}
            </div>
          ) : (
            <div className="space-y-2">
              {groupedColumns.map((col, idx) => (
                <div
                  key={col.id}
                  className="flex items-center gap-2 p-2 bg-gray-50 rounded border"
                  draggable
                  onDragStart={() => setDraggedColumn(col.id)}
                  onDragEnd={() => setDraggedColumn(null)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => {
                    if (draggedColumn && draggedColumn !== col.id) {
                      const fromIdx = groupConfig.columnIds.indexOf(draggedColumn);
                      handleMoveGroupColumn(fromIdx, idx);
                    }
                  }}
                >
                  <GripVertical className="w-4 h-4 text-gray-400 cursor-move" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{col.label}</div>
                    <div className="text-xs text-gray-500">{t.table.groupByPanel.level} {idx + 1}</div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveGroupColumn(col.id)}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Add Column Dropdown */}
          {availableColumns.length > 0 && (
            <Select onValueChange={handleAddGroupColumn}>
              <SelectTrigger className="mt-2">
                <SelectValue placeholder={t.table.groupByPanel.addColumn} />
              </SelectTrigger>
              <SelectContent>
                {availableColumns.map(col => (
                  <SelectItem key={col.id} value={col.id}>
                    {col.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Aggregations */}
        {groupConfig.columnIds.length > 0 && (
          <div>
            <Label className="text-sm font-medium mb-2 block">{t.table.groupByPanel.aggregations}</Label>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {columns
                .filter(col => !col.isHeader)
                .map(col => {
                  const currentAgg = groupConfig.aggregations[col.id] || 'none';
                  
                  return (
                    <div key={col.id} className="flex items-center gap-2">
                      <div className="flex-1 min-w-0 text-sm truncate">{col.label}</div>
                      <Select
                        value={currentAgg}
                        onValueChange={(val) => handleAggregationChange(col.id, val as AggregationType)}
                      >
                        <SelectTrigger className="w-32 h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">{t.table.groupByPanel.none}</SelectItem>
                          <SelectItem value="count">{t.table.groupByPanel.count}</SelectItem>
                          {isAggregationCompatible(col.dataType, 'sum') && (
                            <SelectItem value="sum">{t.table.groupByPanel.sum}</SelectItem>
                          )}
                          {isAggregationCompatible(col.dataType, 'avg') && (
                            <SelectItem value="avg">{t.table.groupByPanel.avg}</SelectItem>
                          )}
                          {isAggregationCompatible(col.dataType, 'min') && (
                            <SelectItem value="min">{t.table.groupByPanel.min}</SelectItem>
                          )}
                          {isAggregationCompatible(col.dataType, 'max') && (
                            <SelectItem value="max">{t.table.groupByPanel.max}</SelectItem>
                          )}
                          <SelectItem value="first">{t.table.groupByPanel.first}</SelectItem>
                          <SelectItem value="last">{t.table.groupByPanel.last}</SelectItem>
                          <SelectItem value="unique_count">{t.table.groupByPanel.unique_count}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* Options */}
        {groupConfig.columnIds.length > 0 && (
          <div className="space-y-2 pt-2 border-t">
            <div className="flex items-center gap-2">
              <Checkbox
                id="subtotals"
                checked={groupConfig.showSubtotals}
                onCheckedChange={handleToggleSubtotals}
              />
              <Label htmlFor="subtotals" className="text-sm cursor-pointer">
                {t.table.groupByPanel.subtotals}
              </Label>
            </div>
            
            <div className="flex items-center gap-2">
              <Checkbox
                id="grandtotal"
                checked={groupConfig.showGrandTotal}
                onCheckedChange={handleToggleGrandTotal}
              />
              <Label htmlFor="grandtotal" className="text-sm cursor-pointer">
                {t.table.groupByPanel.grandTotal}
              </Label>
            </div>
          </div>
        )}

        {/* Actions */}
        {groupConfig.columnIds.length > 0 && (
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearAll}
              className="flex-1"
            >
              {t.table.groupByPanel.reset}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}